// utils/drivenChanges.js
//
// Which elements of a card being saved are BECOMING driven, or are driven
// differently than the saved copy? Those are the ones whose existing item
// values the save will overwrite retroactively (the API backfills every item
// when a drivenFrom config is new or changed), so the editor warns about them
// before the round trip. Pure, so the rule is testable on its own.

import { normalizeId } from './ids';
import { cmp, serialize } from './kql';

const isFormula = config => config?.kind === 'formula';

/** The authored fields of a config, in one comparable shape (the API's sameDrivenConfig). */
function comparable(config) {
	if (!config) return null;
	if (isFormula(config)) {
		return JSON.stringify({ kind: 'formula', formula: config.formula || '', blanks: config.blanks === 'empty' ? 'empty' : 'zero', transform: config.transform?.type || null });
	}
	return JSON.stringify({
		kind: 'related',
		sourceCardId: normalizeId(config.sourceCardId) || '',
		sourceElementName: config.sourceElementName || '',
		linkElementName: config.linkElementName || '',
		aggregate: config.aggregate || 'latest',
		orderBy: config.orderBy || null,
		transform: config.transform?.type || null,
	});
}

/**
 * The elements (with their effective names) whose drivenFrom is new or
 * changed against `savedByName` (name -> saved element). An element that
 * was driven and is no longer is NOT listed: clearing a config overwrites
 * nothing, the computed values simply stay put as ordinary data.
 */
export function changedDrivenElements(elements, savedByName = {}) {
	return (elements || [])
		.map((element, index) => ({ element, index, name: element?.name }))
		.filter(({ element, name }) => name && element?.options?.drivenFrom)
		.filter(({ element, name }) => comparable(element.options.drivenFrom) !== comparable(savedByName?.[name]?.options?.drivenFrom))
		.map(({ element, name }) => ({ name, element }));
}

/**
 * The collection's item list, this card only, filtered to the items that
 * hold a value in the element today — the "review all N items" door of the
 * overwrite warning. The query is the grammar's own serializer over a
 * constructor-built node, never concatenated text.
 */
export function drivenValuesReviewPath(collectionId, cardId, elementName) {
	const params = new URLSearchParams();
	const card = normalizeId(cardId);
	if (card) params.set('card', card);
	if (elementName) params.set('q', serialize(cmp([elementName], 'nempty')));
	const query = params.toString();
	return `/collections/${normalizeId(collectionId) || ''}/items${query ? `?${query}` : ''}`;
}
